import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { format } from "date-fns";
import { Clock, ArrowRight, Ship, ChevronRight, Sailboat } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SearchForm } from "@/components/SearchForm";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { inr } from "@/lib/api";

function dur(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h ? h + "h " : ""}${m ? m + "m" : h ? "" : "0m"}`.trim();
}

export default function SearchResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const from = params.get("from");
  const to = params.get("to");
  const date = params.get("date");
  const pax = params.get("pax") || "1";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cls, setCls] = useState("economy");

  useEffect(() => {
    setLoading(true);
    api
      .get(`/search?from_port=${from}&to_port=${to}&date=${date}&passengers=${pax}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ route: null, schedules: [] }))
      .finally(() => setLoading(false));
  }, [from, to, date, pax]);

  const route = data?.route;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="bg-primary">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6">
          <SearchForm compact />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        {route && (
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <h1 className="font-display font-bold text-2xl text-primary tracking-tight" data-testid="results-title">
              {route.from_port?.city} <ArrowRight className="inline h-5 w-5 text-accent mx-1" /> {route.to_port?.city}
            </h1>
            <span className="text-muted-foreground text-sm">
              {date && format(new Date(date), "EEE, dd MMM yyyy")} · {pax} guest{pax > 1 ? "s" : ""}
            </span>
          </div>
        )}

        {/* Class toggle */}
        <div className="flex items-center gap-2 mb-5">
          {["economy", "business"].map((c) => (
            <button
              key={c}
              onClick={() => setCls(c)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                cls === c ? "bg-primary text-white" : "bg-white border border-border text-muted-foreground hover:text-primary"
              }`}
              data-testid={`class-toggle-${c}`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-28 bg-white rounded-xl border border-border animate-pulse" />
            ))}
          </div>
        ) : !route || data.schedules.length === 0 ? (
          <div className="bg-white border border-border rounded-xl p-12 text-center" data-testid="no-results">
            <Sailboat className="h-10 w-10 text-primary/20 mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-display font-semibold text-lg text-primary">No sailings on this route</h3>
            <p className="text-muted-foreground text-sm mt-1">Try another port pair or head back home to explore popular routes.</p>
            <Button variant="outline" className="mt-5" asChild>
              <Link to="/">Back to home</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {data.schedules.map((s, i) => {
              const price = cls === "economy" ? s.economy_price : s.business_price;
              return (
                <div
                  key={s.id}
                  className="card-lift bg-white border border-border rounded-xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-5 animate-rise"
                  style={{ animationDelay: `${i * 0.05}s` }}
                  data-testid={`schedule-card-${s.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                      <Ship className="h-3.5 w-3.5 text-accent" /> {s.ferry_name}
                    </div>
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-display font-bold text-2xl text-primary">{s.departure_time}</div>
                        <div className="text-xs text-muted-foreground">{s.from_port?.code}</div>
                      </div>
                      <div className="flex-1 flex flex-col items-center min-w-[90px]">
                        <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> {dur(s.duration_min)}</span>
                        <div className="w-full h-px bg-border relative my-1.5">
                          <span className="absolute right-0 -top-1 h-2 w-2 rounded-full bg-accent" />
                        </div>
                      </div>
                      <div>
                        <div className="font-display font-bold text-2xl text-primary">{s.arrival_time}</div>
                        <div className="text-xs text-muted-foreground">{s.to_port?.code}</div>
                      </div>
                    </div>
                  </div>

                  <div className="sm:text-right border-t sm:border-t-0 sm:border-l border-border sm:pl-6 pt-4 sm:pt-0 flex sm:flex-col items-center sm:items-end justify-between gap-3">
                    <div>
                      <Badge variant="secondary" className="capitalize mb-1">{cls}</Badge>
                      <div className="font-display font-bold text-xl text-primary" data-testid={`price-${s.id}`}>{inr(price)}</div>
                      <div className="text-[11px] text-muted-foreground">per person</div>
                    </div>
                    <Button
                      className="bg-accent hover:bg-accent/90 text-white font-semibold"
                      onClick={() => navigate(`/book/${s.id}?class=${cls}&date=${date}&pax=${pax}`)}
                      data-testid={`select-btn-${s.id}`}
                    >
                      Select <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
